// VenuePaymentCancelled.tsx
// ✅ COMPLETE VERSION with all features
// ✅ Enhanced error handling and user guidance
// ✅ Works with URL query params from backend redirect
// ✅ Detailed troubleshooting tips
// ✅ Support contact information

import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, Home, RefreshCw, Phone, Mail, HelpCircle } from 'lucide-react';

// ====================================
// TYPE DEFINITIONS
// ====================================

interface StatusInfo {
  title: string;
  message: string;
  icon: 'warning' | 'error';
  color: 'orange' | 'red';
  amountDeducted: boolean;
}

// ====================================
// MAIN COMPONENT
// ====================================

const VenuePaymentCancelled: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get URL parameters
  const orderId = searchParams.get('orderId');
  const status = searchParams.get('status');
  const reason = searchParams.get('reason');

  useEffect(() => {
    console.log('❌ Payment Cancelled/Failed Page', { orderId, status, reason });
  }, [orderId, status, reason]);

  // ====================================
  // STATUS DETERMINATION
  // ====================================

  const getStatusInfo = (): StatusInfo => {
    const statusLower = status?.toLowerCase() || '';

    // User cancelled/aborted payment
    if (statusLower === 'aborted' || statusLower === 'cancelled') {
      return {
        title: 'Payment Cancelled',
        message: 'You cancelled the payment transaction.',
        icon: 'warning',
        color: 'orange',
        amountDeducted: false,
      };
    }
    
    // Payment failed
    if (statusLower === 'failure' || statusLower === 'failed') {
      return {
        title: 'Payment Failed',
        message: reason || 'Your payment could not be processed. Please try again or use a different payment method.',
        icon: 'error',
        color: 'red',
        amountDeducted: false,
      };
    }

    // Payment error
    if (statusLower === 'error') {
      return {
        title: 'Payment Error',
        message: reason || 'An error occurred while processing your payment. Please try again.',
        icon: 'error',
        color: 'red',
        amountDeducted: false,
      };
    }
    
    // Unknown/default status
    return {
      title: 'Payment Not Completed',
      message: reason || 'The venue booking payment was not completed. Please try again.',
      icon: 'error',
      color: 'red',
      amountDeducted: false,
    };
  };

  const statusInfo = getStatusInfo();

  // ====================================
  // RENDER
  // ====================================

  return (
    <div className={`min-h-screen bg-gradient-to-br ${
      statusInfo.color === 'orange' 
        ? 'from-orange-50 via-white to-yellow-50' 
        : 'from-red-50 via-white to-pink-50'
    } flex items-center justify-center p-4`}>
      <div className="glass-card rounded-3xl max-w-2xl w-full p-8">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className={`w-20 h-20 ${
            statusInfo.color === 'orange' ? 'bg-orange-100' : 'bg-red-100'
          } rounded-full flex items-center justify-center`}>
            <AlertTriangle className={`h-12 w-12 ${
              statusInfo.color === 'orange' ? 'text-orange-600' : 'text-red-600'
            }`} />
          </div>
        </div>

        {/* Title and Message */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {statusInfo.title}
          </h1>
          <p className="text-gray-600 text-lg">
            {statusInfo.message}
          </p>
        </div>

        {/* Order Details */}
        {orderId && (
          <div className="glass rounded-xl p-4 mb-6 border-2 border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Order ID</p>
                <p className="font-mono font-semibold text-gray-900">{orderId}</p>
              </div>
              <div>
                <p className="text-gray-500">Status</p>
                <p className="font-semibold text-gray-900">{status || 'Unknown'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Important Notice */}
        <div className={`glass rounded-xl p-4 mb-6 border-2 ${
          statusInfo.color === 'orange' 
            ? 'border-orange-200 bg-orange-50' 
            : 'border-red-200 bg-red-50'
        }`}>
          <div className="flex items-start space-x-3">
            <HelpCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
              statusInfo.color === 'orange' ? 'text-orange-600' : 'text-red-600'
            }`} />
            <div className="flex-1">
              <p className={`font-semibold mb-2 ${
                statusInfo.color === 'orange' ? 'text-orange-900' : 'text-red-900'
              }`}>
                Important Information
              </p>
              <p className={`text-sm ${
                statusInfo.color === 'orange' ? 'text-orange-800' : 'text-red-800'
              }`}>
                {statusInfo.amountDeducted ? (
                  <>
                    If an amount has been deducted from your account, it will be automatically refunded 
                    within 5-7 working days. For immediate assistance, please contact HMDA support with your 
                    order ID.
                  </>
                ) : (
                  <>
                    No amount has been deducted from your account. Your venue booking has not been confirmed. 
                    Please try again to complete the booking.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 mb-6">
          <button
            onClick={() => navigate('/venues')}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-xl transform hover:scale-105 transition-all flex items-center justify-center"
          >
            <RefreshCw className="h-5 w-5 mr-2" />
            Try Booking Again
          </button>
          
          <button
            onClick={() => navigate('/bookings')}
            className="w-full glass py-4 px-6 rounded-xl font-semibold hover:bg-white/50 transition-all"
          >
            View My Bookings
          </button>

          <button
            onClick={() => navigate('/')}
            className="w-full glass py-4 px-6 rounded-xl font-semibold hover:bg-white/50 transition-all flex items-center justify-center"
          >
            <Home className="h-5 w-5 mr-2" />
            Back to Home
          </button>
        </div>

        {/* Support Section */}
        <div className="glass rounded-xl p-6 bg-blue-50 border-2 border-blue-200 mb-6">
          <p className="text-sm font-semibold text-gray-900 mb-4 text-center">
            Need Assistance?
          </p>
          <div className="space-y-3 text-sm">
            <div className="flex flex-col md:flex-row items-center justify-center md:space-x-4 space-y-2 md:space-y-0">
              <div className="flex items-center text-blue-600">
                <Phone className="h-4 w-4 mr-1" />
                <span className="font-semibold">040-23401234</span>
              </div>
              <span className="hidden md:block text-gray-400">|</span>
              <div className="flex items-center text-blue-600">
                <Phone className="h-4 w-4 mr-1" />
                <span className="font-semibold">1800-425-0102 (Toll-free)</span>
              </div>
            </div>
            <div className="flex items-center justify-center text-blue-600">
              <Mail className="h-4 w-4 mr-1" />
              <span className="font-semibold">venues@hmda.gov.in</span>
            </div>
          </div>
          <p className="text-xs text-gray-600 text-center mt-4">
            Working Hours: Monday to Saturday, 10:00 AM - 5:00 PM
          </p>
        </div>

        {/* Troubleshooting Tips */}
        <div className="glass rounded-xl p-6 border-2 border-gray-200">
          <div className="flex items-center mb-4">
            <HelpCircle className="h-5 w-5 text-blue-600 mr-2" />
            <p className="text-sm font-semibold text-gray-900">
              Common Issues & Solutions
            </p>
          </div>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span><strong>Network Error:</strong> Check your internet connection and try again</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span><strong>Insufficient Funds:</strong> Ensure you have sufficient balance in your account</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span><strong>Payment Method:</strong> Try using a different card or payment method</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span><strong>Browser Issues:</strong> Clear browser cache and cookies, or try a different browser</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span><strong>Bank Restrictions:</strong> Contact your bank if payment repeatedly fails</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span><strong>Daily Limit:</strong> Check if you've exceeded your daily transaction limit</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span><strong>OTP Issues:</strong> Ensure you're entering the correct OTP within the time limit</span>
            </li>
          </ul>
        </div>

        {/* Additional Help */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500 mb-2">
            For technical support, email us with your order ID and payment details
          </p>
          <p className="text-xs text-gray-400">
            Reference ID: {orderId || 'N/A'} | Status: {status || 'Unknown'} | 
            Timestamp: {new Date().toLocaleString('en-IN')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VenuePaymentCancelled;